import {Button} from '@wandb/components';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {loadArtifactMembershipFilePathMetadata} from '../../../files';
import {useApolloClient} from '../../../state/hooks';
import {DownloadArtifactDrawer} from './DownloadArtifactDrawer';
import {
  DownloadStateEnum,
  useDownloadArtifactStore,
  useDownloadArtifactStoreCanDownload,
  useDownloadArtifactStoreDownloadState,
  useDownloadArtifactStoreInit,
  useDownloadArtifactStoreMaybeStartArtifactZipBuild,
  useDownloadArtifactStoreReset,
  useDownloadArtifactStoreSetArtifactFiles,
} from './downloadArtifactStore';
import {flattenFilesMetadata} from './utils';

type DownloadArtifactButtonProps = {
  artifactCollectionName: string;
  entityName: string;
  projectName: string;
  versionId: string;
  versionIndex: number;
  artifactSize: number;
  fileCount: number;
};

export const DownloadArtifactButton: React.FC<DownloadArtifactButtonProps> = ({
  artifactCollectionName,
  entityName,
  projectName,
  versionId,
  versionIndex,
  artifactSize,
  fileCount,
}) => {
  const client = useApolloClient();

  const initDownloadArtifactStore = useDownloadArtifactStoreInit();
  const resetDownloadArtifactStore = useDownloadArtifactStoreReset();

  const canDownload = useDownloadArtifactStoreCanDownload();
  const downloadState = useDownloadArtifactStoreDownloadState();
  const setArtifactFiles = useDownloadArtifactStoreSetArtifactFiles();
  const maybeStartArtifactZipBuild =
    useDownloadArtifactStoreMaybeStartArtifactZipBuild();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Closing the drawer restores focus to the trigger, which leaves it styled
    // as focused. Blur it unless it matches :focus-visible, so keyboard users
    // keep their focus ring.
    requestAnimationFrame(() => {
      const button = buttonRef.current;
      if (
        button != null &&
        document.activeElement === button &&
        !button.matches(':focus-visible')
      ) {
        button.blur();
      }
    });
  }, []);

  useEffect(() => {
    const zipFileName = `${artifactCollectionName}_v${versionIndex}.zip`;
    initDownloadArtifactStore(artifactSize, fileCount, zipFileName);

    // Flag to indicate that the component has been unmounted and the session has been reset
    // so we can bail out of the async function and not set any state
    let cancelled = false;

    // canDownload from the hook closure is stale here, so we read it from the store state.
    const canDownload = useDownloadArtifactStore.getState().canDownload;

    if (canDownload) {
      async function getManifestAndFileTree() {
        // loadArtifactMembershipFilePathMetadata takes in artifact membership id, but only uses it as a cache key.
        // we construct our own cache key so we don't have to fetch the membership id.
        const cacheKey = `${entityName}/${projectName}/${artifactCollectionName}/${versionId}`;
        const manifest = await loadArtifactMembershipFilePathMetadata(
          client,
          cacheKey,
          entityName,
          projectName,
          artifactCollectionName,
          `v${versionIndex}`,
          ''
        );
        if (cancelled) {
          return;
        }
        if (manifest == null) {
          setArtifactFiles(null);
          return;
        }
        const files = flattenFilesMetadata(manifest);
        const hasReferenceFiles = files.some(f => !!f.ref);
        setArtifactFiles(files, hasReferenceFiles);
      }
      getManifestAndFileTree();
    }

    return () => {
      cancelled = true;
      resetDownloadArtifactStore();
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  // The button is disabled if the artifact can be downloaded and the files are still loading
  const buttonDisabled =
    canDownload && downloadState === DownloadStateEnum.PENDING;

  return (
    <>
      <Button
        ref={buttonRef}
        size="md"
        variant="secondary"
        isDisabled={buttonDisabled}
        onClick={() => {
          setDrawerOpen(true);
          maybeStartArtifactZipBuild();
        }}>
        Download
      </Button>
      <DownloadArtifactDrawer open={drawerOpen} onClose={closeDrawer} />
    </>
  );
};
